import React, { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { validatePassword, validateEmail } from "../utils/validation";
import { Alert } from "../components/Alert";
import { AuthService } from "../services/authService";

interface Message {
  text: string;
  type: 'error' | 'success';
}

type FieldErrors = Record<string, boolean>;

export const Register: React.FC = () => {
    const alertRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [agreedToTos, setAgreedToTos] = useState<boolean>(false);
    const [message, setMessage] = useState<Message | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const clearFieldError = (fieldName: string): void => {
        if (fieldErrors[fieldName]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFirstName(e.target.value);
        clearFieldError('firstName');
    };

    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setLastName(e.target.value);
        clearFieldError('lastName');
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target.value);
        clearFieldError('email');
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setPassword(e.target.value);
        clearFieldError('password');
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setConfirmPassword(e.target.value);
        clearFieldError('confirmPassword');
    };

    const handleTosChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setAgreedToTos(e.target.checked);
        clearFieldError('agreedToTos');
    };

    const validateForm = (): { errors: FieldErrors; errorMessage: string } => {
        const errors: FieldErrors = {};
        let firstErrorMessage: string = '';

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();

        if (!trimmedFirstName) {
            errors.firstName = true;
            if (!firstErrorMessage) firstErrorMessage = 'First name is required';
        }

        if (!trimmedLastName) {
            errors.lastName = true;
            if (!firstErrorMessage) firstErrorMessage = 'Last name is required';
        }

        const emailError = validateEmail(email.trim());
        if (emailError) {
            errors.email = true;
            if (!firstErrorMessage) firstErrorMessage = emailError;
        }

        const passwordError = validatePassword(password, confirmPassword);
        if (passwordError) {
            errors.password = true;
            errors.confirmPassword = true;
            if (!firstErrorMessage) firstErrorMessage = passwordError;
        }

        if (!agreedToTos) {
            errors.agreedToTos = true;
            if (!firstErrorMessage) firstErrorMessage = 'You must agree to the Terms of Service to register.';
        }

        return { errors, errorMessage: firstErrorMessage };
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);
        setFieldErrors({});

        const { errors, errorMessage } = validateForm();

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setMessage({
                text: errorMessage || 'Please correct the highlighted fields.',
                type: 'error'
            });
            setIsLoading(false);
            return;
        }
        try {
            const trimmedEmail = email.trim().toLowerCase();
            const trimmedFirstName = firstName.trim();
            const trimmedLastName = lastName.trim();

            const result = await AuthService.register({
                email: trimmedEmail,
                password,
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                agreedToTos,
                ...(token && { token })
            });

            navigate('/login', {
                replace: true,
                state: {
                    message: result.message ?? 'Registration successful. Please log in.',
                    email: trimmedEmail,
                },
            });
        } catch (err: unknown) {
            const errorText = err instanceof Error ? err.message : 'Registration failed. Please try again.';
            setMessage({ text: errorText, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const getInputClass = (fieldName: string): string => {
        return `w-full input ${fieldErrors[fieldName] ? 'input-error' : ''}`;
    };

    return (
        <div className="reg-layout">
            <div className="lcont max-w-xl">
                <div ref={alertRef}>
                    {message?.text && (
                        <Alert type={message.type} message={message.text} />
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <fieldset className="fieldset">
                      <legend className="fieldset-legend">First name</legend>
                      <input
                          type="text"
                          placeholder="Ada"
                          className={getInputClass('firstName')}
                          value={firstName}
                          onChange={handleFirstNameChange}
                          autoComplete="given-name"
                        />
                    </fieldset>

                    <fieldset className="fieldset">
                      <legend className="fieldset-legend">Last name</legend>
                      <input
                          type="text"
                          placeholder="Lovelace"
                          className={getInputClass('lastName')}
                          value={lastName}
                          onChange={handleLastNameChange}
                          autoComplete="family-name"
                        />
                    </fieldset>

                    <fieldset className="fieldset">
                      <legend className="fieldset-legend">Email</legend>
                      <input
                          type="email"
                          placeholder="you@researchcentre.org"
                          className={getInputClass('email')}
                          value={email}
                          onChange={handleEmailChange}
                          aria-describedby="email-help"
                          autoComplete="email"
                        />
                    </fieldset>

                    <fieldset className="fieldset">
                      <legend className="fieldset-legend">Password</legend>
                      <input
                          type="password"
                          placeholder="••••••••"
                          className={getInputClass('password')}
                          value={password}
                          onChange={handlePasswordChange}
                      />
                        <p className="label">Must be 8+ characters with uppercase, lowercase, and number</p>
                    </fieldset>
                    <fieldset className="fieldset">
                      <legend className="fieldset-legend">Confirm Password</legend>
                      <input
                          type="password"
                          placeholder="••••••••"
                          className={getInputClass('confirmPassword')}
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                        />
                    </fieldset>

                    <div className="space-y-2">
                        <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                className={`checkbox ${fieldErrors.agreedToTos ? 'checkbox-error' : ''}`}
                                checked={agreedToTos}
                                onChange={handleTosChange}
                            />
                            <span className="text-sm">
                                I agree to the <Link to="/terms" className="text-primary hover:underline" target="_blank">Terms of Service</Link> and&nbsp;
                              <Link to="/privacy" className="text-primary hover:underline" target="_blank">Privacy Policy</Link>
                            </span>
                        </label>
                    </div>

                    <button type="submit" className="w-full btn btn-primary" disabled={isLoading}>
                        {isLoading ? <span className="loading loading-spinner"></span> : 'Register'}
                    </button>
                </form>
                <p className="text-sm text-center">
                    Already have an account? <Link to="/login" className="text-primary hover:underline">Login</Link>
                </p>
            </div>
        </div>
    );
};
